import { useAuthContext } from '@/context/auth-context';
import { Button } from '@lego/connect-components-react';
import { Container, Flex, Text } from '@lego/klik-ui';
import type { ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';

type UnauthorizedLocationState = {
  reason?: 'app' | 'feature';
};

export const UnauthorizedPage = (): ReactElement => {
  const { logout, user, isLoading } = useAuthContext();
  const location = useLocation();
  const state = location.state as UnauthorizedLocationState;
  const reason = state?.reason;

  const getMessageCopy = () => {
    if (reason === 'app') {
      return {
        title: 'Access Denied',
        body: 'You do not have access to this application.',
        detail: 'Please contact your administrator to request access to LPPS.',
      };
    }

    if (reason === 'feature') {
      return {
        title: 'Unauthorized Access',
        body: 'You do not have permission to access this feature.',
        detail:
          'Please contact the development team if you believe this is an error.',
      };
    }

    // Default fallback message
    return {
      title: 'Unauthorized Access',
      body: 'You do not have permission to access this application.',
      detail:
        'Please contact the development team if you believe this is an error.',
    };
  };

  const copy = getMessageCopy();

  return (
    <Container
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      <Text fontSize="2xl" fontWeight="bold" color="red.500">
        {copy.title}
      </Text>
      <Text marginTop="16px">{copy.body}</Text>
      <Text marginTop="8px" marginBottom="24px">
        {copy.detail}
      </Text>
      <Flex gap="16px">
        {reason === 'feature' && (
          <Link to="/">
            <Button variant="primary" aria-label="Go back to home">
              Go Back Home
            </Button>
          </Link>
        )}
        <Button onClick={logout} variant="primary" aria-label="Logout">
          Logout
        </Button>
      </Flex>
    </Container>
  );
};
